/**
 * Utility helpers to detect browser environment (Instagram, In-App WebViews, Standalone PWA, Tablets).
 */

export const isInstagramBrowser = (): boolean => {
  if (typeof window === "undefined") return false;
  const ua = navigator.userAgent || navigator.vendor || (window as any).opera || "";
  return /Instagram|FBAN|FBAV|FB_IAB|TikTok|Twitter|Line/i.test(ua);
};

/**
 * Detects whether the current device is a restaurant tablet (e.g. Nokia T20, Samsung Galaxy Tab S2, iPad, or any tablet).
 * Customers on mobile phones (iPhone, Android mobile phones) will ALWAYS return FALSE.
 */
export const isTabletDevice = (): boolean => {
  if (typeof window === "undefined") return false;

  // 1. Explicit tablet override via localStorage or URL query param (e.g. ?tablet=1)
  try {
    if (localStorage.getItem("is_sewar_tablet") === "true") return true;

    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get("tablet") === "1" || urlParams.get("tablet") === "true") {
      localStorage.setItem("is_sewar_tablet", "true");
      return true;
    }
  } catch {}

  const ua = (navigator.userAgent || navigator.vendor || (window as any).opera || "").toLowerCase();

  // 2. Strict mobile phone check: Mobile phones (iPhone, iPod, Android Mobile phones) ALWAYS have "mobile" or "iphone"
  // If it's a mobile phone, it is 100% a customer phone -> return false immediately!
  const isMobilePhone = /iphone|ipod/i.test(ua) || (/android/i.test(ua) && /mobile/i.test(ua));
  if (isMobilePhone) {
    return false;
  }

  // 3. Known restaurant tablet hardware models:
  // - Nokia T20 (TA-1392, TA-1394, TA-1397, Nokia T20)
  // - Samsung Galaxy Tab S2 (SM-T810, SM-T813, SM-T815, SM-T819, SM-T710, SM-T713, SM-T715, etc.)
  const isKnownRestaurantTablet =
    /nokia\s*t20|ta-139[0-9]|sm-t81[0-9]|sm-t813|sm-t815|sm-t819|sm-t71[0-9]|galaxy\s*tab|samsung.*tab/i.test(ua);
  if (isKnownRestaurantTablet) {
    try { localStorage.setItem("is_sewar_tablet", "true"); } catch {}
    return true;
  }

  // 4. Android Tablets (Google Chrome Android tablet specification: has "android" but does NOT contain "mobile")
  const isAndroidTablet = /android/i.test(ua) && !/mobile/i.test(ua);
  if (isAndroidTablet) {
    try { localStorage.setItem("is_sewar_tablet", "true"); } catch {}
    return true;
  }

  // 5. iPad detection (iPadOS)
  const isIPad = /ipad/i.test(ua) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
  if (isIPad) {
    try { localStorage.setItem("is_sewar_tablet", "true"); } catch {}
    return true;
  }

  // 6. Touch Tablet Screen Dimension check (physical dimension >= 600px, e.g. 800x1280, 1200x2000, 1536x2048)
  const hasTouch = "ontouchstart" in window || (navigator.maxTouchPoints && navigator.maxTouchPoints > 0);
  const minDimension = Math.min(window.screen.width, window.screen.height);
  if (hasTouch && minDimension >= 600) {
    try { localStorage.setItem("is_sewar_tablet", "true"); } catch {}
    return true;
  }

  return false;
};
